import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { PaymentStripeTaxWebService } from '@vsolv/packages/payments/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { SecurityService } from '@wsphere/staff/web';
import { BehaviorSubject, map, switchMap, tap } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-stripe-tax-dialog',
  templateUrl: './stripe-tax.dialog.html',
  standalone: false,
})
export class StripeTaxDialog {
  private paymentStripeTaxSvc = inject(PaymentStripeTaxWebService);
  private securitySvc = inject(SecurityService);
  private toastSvc = inject(ToastService);

  protected secretKeyControl = new FormControl(null as string | null);

  private refresh$ = new BehaviorSubject<null>(null);

  isEnabled = false;

  protected key$ = this.refresh$.pipe(
    switchMap(() => this.paymentStripeTaxSvc.retrieve()),
    map(response => {
      this.secretKeyControl.setValue(response.key);
      return response.key;
    }),
    tap(key => (this.isEnabled = !!key))
  );

  protected readonly canManageStripeTax$ = this.securitySvc.globalDistributors$.pipe(
    switchMap(globalDistributors => {
      const permissionKeys = globalDistributors?.map(dist => dist.permissionKey) ?? null;
      return this.securitySvc.hasAccess('stf_StripeTaxApp', permissionKeys);
    }),
    tap(canManage => (!canManage ? this.secretKeyControl.disable() : this.secretKeyControl.enable()))
  );

  protected async enable(secretKey: string | null) {
    if (!secretKey) {
      this.toastSvc.show({
        type: 'error',
        title: 'Error',
        text: 'Unable to enable the Stripe Tax integration.',
      });
      return;
    }
    try {
      const response = await this.paymentStripeTaxSvc.create({ secretKey });
      this.refresh$.next(null);
      if (response.key) {
        this.toastSvc.show({
          type: 'success',
          title: 'Stripe Tax integration app enabled',
          text: 'This application has been enabled. All of its features will be accessible unless app is disabled.',
        });
      }
    } catch (error) {
      this.toastSvc.show({
        type: 'error',
        title: 'Error',
        text: 'Unable to enable the Stripe Tax integration.',
      });
    }
  }

  protected async disable() {
    try {
      const response = await this.paymentStripeTaxSvc.delete();
      this.refresh$.next(null);
      if (response.tenantId) {
        this.toastSvc.show({
          type: 'success',
          title: 'Stripe Tax integration app disabled',
          text: 'This application has been disabled. None of its features will be accessible until enabled again.',
        });
      }
    } catch (error) {
      this.toastSvc.show({
        type: 'error',
        title: 'Error',
        text: 'Unable to disable the Stripe Tax integration.',
      });
    }
  }

  protected async update(secretKey: string | null) {
    if (!secretKey) {
      this.toastSvc.show({
        type: 'error',
        title: 'Error',
        text: 'Unable to update the Stripe Tax integration.',
      });
      return;
    }
    try {
      const response = await this.paymentStripeTaxSvc.update({ secretKey });
      this.refresh$.next(null);
      if (response.key) {
        this.toastSvc.show({
          type: 'success',
          title: 'Stripe Tax integration app updated',
          text: 'This application has been updated. All of its features will be accessible unless app is disabled.',
        });
      }
    } catch (error) {
      this.toastSvc.show({
        type: 'error',
        title: 'Error',
        text: 'Unable to update the Stripe Tax integration.',
      });
    }
  }
}
